import * as _ from 'lodash-es';
import QuillBlock, { BlockEmbed } from 'quill/blots/block';
import Break from 'quill/blots/break';
import Container from 'quill/blots/container';
import Cursor from 'quill/blots/cursor';
import QuillEmbed from 'quill/blots/embed';
import Inline from 'quill/blots/inline';
import QuillScroll from 'quill/blots/scroll';
import TextBlot from 'quill/blots/text';
import type { Parchment } from 'quill/core';
import Quill from 'quill/core';
import Bold from 'quill/formats/bold';
import Indent from 'quill/formats/indent';
import Italic from 'quill/formats/italic';
import List, { ListContainer } from 'quill/formats/list';
import Strike from 'quill/formats/strike';
import QuillKeyboard from 'quill/modules/keyboard';
import QuillTableEmbed from 'quill/modules/tableEmbed';
import QuillUploader from 'quill/modules/uploader';
import AssetUploader from './AssetUploader';
import Clipboard from './Clipboard';
import HeaderIdModule from './HeaderId';
import History from './History';
import HoveredBlotListener from './HoveredBlotListener';
import Input from './Input';
import Keyboard from './Keyboard';
import PrefixListener from './PrefixListener';
import RangeSaver from './RangeSaver';
import Uploader from './Uploader';
import CodeEmbedEditorTheme from './code/CodeEmbedEditorTheme';
import Annotation from './formats/Annotation';
import Attachment from './formats/Attachment';
import AttachmentMention from './formats/AttachmentMention';
import BasicHeader from './formats/BasicHeader';
import Block from './formats/Block';
import Blockquote, { BlockquoteContainer } from './formats/Blockquote';
import ButtonBlot from './formats/ButtonBlot';
import Card from './formats/Card';
import CellImageBlot from './formats/CellImage';
import CodeEmbed from './formats/CodeEmbed';
import CollapsibleListItem from './formats/CollapsibleListItem';
import Comment from './formats/Comment';
import Diff from './formats/Diff';
import Embed from './formats/Embed';
import FileBlot from './formats/FileBlot';
import Header from './formats/Header';
import HeaderCollapse from './formats/HeaderCollapse';
import HeaderId from './formats/HeaderId';
import Height from './formats/Height';
import Highlight from './formats/Highlight';
import Hint from './formats/Hint';
import HistoricalCodeEmbed from './formats/HistoricalCodeEmbed';
import HorizontalRule from './formats/HorizontalRule';
import IFrame from './formats/IFrame';
import ImageBlot from './formats/ImageBlot';
import Code from './formats/InlineCode';
import Link from './formats/Link';
import ListCollapse from './formats/ListCollapse';
import Mention from './formats/Mention';
import Reaction from './formats/Reaction';
import Scroll from './formats/Scroll';
import SearchFacet from './formats/SearchFacet';
import SoftBreak from './formats/SoftBreak';
import Span from './formats/Span';
import TableEmbed from './formats/TableEmbed';
import UserSequence from './formats/UserSequence';
import VersionChange from './formats/VersionChange';
import Video from './formats/Video';
import AnnotationMention from './formats/namespaces/annotation/AnnotationMention';
import CodeEmbedFont from './formats/namespaces/code/CodeEmbedFont';
import CodeEmbedToken from './formats/namespaces/code/CodeEmbedToken';
import CommentMention from './formats/namespaces/comment/CommentMention';
import EmbeddedLink from './formats/namespaces/embedPostContent/EmbeddedLink';
import EmbeddedTableEmbed from './formats/namespaces/embedPostContent/EmbeddedTableEmbed';
import EmbeddedHeader from './formats/namespaces/embedded/EmbeddedHeader';
import EmbeddedMention from './formats/namespaces/embedded/EmbeddedMention';
import DelayedRenderBlockGroup from './formats/namespaces/postPreview/DelayedRenderBlockGroup';
import PreviewEmbed from './formats/namespaces/postPreview/PreviewEmbed';
import PreviewHeader from './formats/namespaces/postPreview/PreviewHeader';
import PreviewMention from './formats/namespaces/postPreview/PreviewMention';
import SearchInputEditorTheme from './search/SearchInputEditorTheme';
import TableCellEditorTheme from './table/cell/TableCellEditorTheme';
import { registerTableCellFormats } from './table/convertFromNode';

Quill.register('themes/tableCellEditorTheme', TableCellEditorTheme);
Quill.register('themes/codeEmbedEditorTheme', CodeEmbedEditorTheme);
Quill.register('themes/searchInputEditorTheme', SearchInputEditorTheme);

const coreFormats = {
  'blots/block': QuillBlock,
  'blots/block/embed': BlockEmbed,
  'blots/break': Break,
  'blots/container': Container,
  'blots/cursor': Cursor,
  'blots/scroll': QuillScroll,
  'blots/embed': QuillEmbed,
  'blots/inline': Inline,
  'blots/text': TextBlot,

  'modules/clipboard': Clipboard,
  'modules/history': History,
  'modules/keyboard': QuillKeyboard,
  'modules/input': Input,
  'modules/uploader': QuillUploader,
  'modules/tableEmbed': QuillTableEmbed,
  'modules/rangeSaver': RangeSaver,
};

const commonFormats = {
  'formats/link': Link,
  'formats/bold': Bold,
  'formats/italic': Italic,
  'formats/strike': Strike,
  'formats/code': Code,
};

const postFormats = {
  ...coreFormats,
  ...commonFormats,
  'blots/block': Block,
  'blots/scroll': Scroll,
  'blots/embed': Embed,

  'blots/list-container': ListContainer,
  'formats/list': CollapsibleListItem,
  'formats/list-collapse': ListCollapse,
  'formats/indent': Indent,

  'formats/header': BasicHeader,
  'formats/blockquote': Blockquote,
  'formats/blockquote-container': BlockquoteContainer,

  'formats/highlight': Highlight,
  'formats/hint': Hint,

  'formats/hr': HorizontalRule,
  'formats/embed': Embed,
  'formats/card': Card,
  'formats/iframe': IFrame,
  'formats/image': ImageBlot,
  'formats/file': FileBlot,
  'formats/span': Span,
  'formats/height': Height,
  'formats/attachment': Attachment,
  'formats/mention': Mention,
  'formats/attachment-mention': AttachmentMention,
  'formats/video': Video,
  'formats/soft': SoftBreak,

  'modules/clipboard': Clipboard,
  'modules/keyboard': Keyboard,

  'modules/uploader': Uploader,
  'modules/prefixListener': PrefixListener,
  'modules/hoveredBlotListener': HoveredBlotListener,
  [`modules/${AssetUploader.moduleName}`]: AssetUploader,
  'formats/table-embed': TableEmbed,
  'formats/code-embed': CodeEmbed,
  'formats/button': ButtonBlot,
};

const headerCollapseFormats = {
  'formats/header-collapse': HeaderCollapse,
  'formats/header-id': HeaderId,
};

const editorFormats = {
  ...postFormats,
  ...headerCollapseFormats,
  'formats/header': Header,
};

Quill.register({
  'modules/headerId': HeaderIdModule,
});

const magneticWallFormats = {
  'formats/comment': Comment,
  'formats/annotation': Annotation,
  'formats/reaction': Reaction,
};

const historyFormats = {
  'formats/diff': Diff,
  'formats/version-change': VersionChange,
  'formats/user-sequence': UserSequence,
};

const topicUserDescriptionFormats = {
  ...coreFormats,
  ...commonFormats,
  'blots/list-container': ListContainer,

  'formats/list': List,
  'formats/indent': Indent,

  'formats/hint': Hint,

  'formats/hr': HorizontalRule,
  'formats/mention': Mention,
  'formats/soft': SoftBreak,

  'formats/highlight': Highlight,

  'modules/clipboard': Clipboard,
  'modules/keyboard': Keyboard,

  'modules/prefixListener': PrefixListener,
};

Quill.register(
  {
    ...editorFormats,
    ...magneticWallFormats,
    ...historyFormats,
  },
  true,
);

const postPreviewFormats = {
  ...postFormats,
  'formats/mention': PreviewMention,
  'formats/header': PreviewHeader,
  'formats/header-id': HeaderId,
  'formats/embed': PreviewEmbed,
  'formats/delayed-render-block-group': DelayedRenderBlockGroup,
};

const tableCellFormats = {
  ...coreFormats,
  ...commonFormats,
  ...magneticWallFormats,
  ...historyFormats,
  'formats/soft': SoftBreak,

  'blots/list-container': ListContainer,
  'formats/list': List,
  'formats/indent': Indent,
  'formats/mention': Mention,
  'formats/header': BasicHeader,
  'formats/highlight': Highlight,

  'formats/blockquote': Blockquote,
  'formats/blockquote-container': BlockquoteContainer,

  'modules/uploader': Uploader,
  'modules/prefixListener': PrefixListener,
  [`modules/${AssetUploader.moduleName}`]: AssetUploader,

  'formats/image': CellImageBlot,
};

const namespaceFormats = _.mapValues(
  {
    editor: {
      ...editorFormats,
      ...magneticWallFormats,
    },
    postPreview: {
      ...postPreviewFormats,
    },
    embedPostContent: {
      ...postPreviewFormats,
      ...headerCollapseFormats,
      'formats/list-collapse': List,
      'formats/mention': EmbeddedMention,
      'formats/link': EmbeddedLink,
      'formats/table-embed': EmbeddedTableEmbed,
      'formats/header': EmbeddedHeader,
    },
    extensionPostCard: {
      ...postPreviewFormats,
      ...headerCollapseFormats,
      ...magneticWallFormats,
      'formats/list-collapse': List,
      'formats/mention': EmbeddedMention,
      'formats/link': EmbeddedLink,
      'formats/table-embed': EmbeddedTableEmbed,
      'formats/header': EmbeddedHeader,
    },
    embedded: {
      ...editorFormats,
      'modules/hoveredBlotListener': HoveredBlotListener,
      'formats/list-collapse': List,
      'formats/link': EmbeddedLink,
      'formats/mention': EmbeddedMention,
      'formats/header': EmbeddedHeader,
      'formats/table-embed': EmbeddedTableEmbed,
    },
    embeddedTableCell: {
      ...tableCellFormats,
      'formats/link': EmbeddedLink,
      'formats/mention': EmbeddedMention,
    },
    history: {
      ...postFormats,
      ...historyFormats,
      ...headerCollapseFormats,
      'modules/hoveredBlotListener': HoveredBlotListener,
      'formats/header': Header,
      'formats/comment': Comment,
      'formats/annotation': Annotation,
      'formats/code-embed': HistoricalCodeEmbed,
    },
    publicEditor: {
      ...editorFormats,
      ...magneticWallFormats,
      'modules/hoveredBlotListener': HoveredBlotListener,
    },
    tableCell: {
      ...tableCellFormats,
    },
    code: {
      ...coreFormats,
      ...magneticWallFormats,
      ...historyFormats,
      'formats/code-embed-token': CodeEmbedToken,
      'formats/code-embed-font': CodeEmbedFont,
      'formats/link': Link,
      'formats/bold': Bold,
      'formats/italic': Italic,
      'formats/strike': Strike,
      'formats/highlight': Highlight,
    },
    comment: {
      ...coreFormats,
      ...commonFormats,
      'formats/mention': CommentMention,

      'blots/list-container': ListContainer,
      'formats/list': List,
      'formats/indent': Indent,
    },
    copliot: {
      ...coreFormats,
      'formats/link': Link,
      'formats/mention': CommentMention,
      'modules/prefixListener': PrefixListener,
    },
    annotation: {
      ...coreFormats,
      ...commonFormats,
      'formats/mention': AnnotationMention,

      'blots/list-container': ListContainer,
      'formats/list': List,
      'formats/indent': Indent,
    },
    searchInput: {
      ...coreFormats,

      'formats/search-facet': SearchFacet,
    },
    topicUserDescription: {
      ...topicUserDescriptionFormats,
    },
    organizationSummary: {
      ...topicUserDescriptionFormats,
      'formats/header': BasicHeader,
    },
  },
  (definition) => {
    return Object.entries(definition)
      .filter(([name, target]) => {
        return (
          (name.startsWith('blots/') || name.startsWith('formats/')) &&
          target.blotName !== 'abstract'
        );
      })
      .map(([, target]) => target as Parchment.RegistryDefinition);
  },
);

export type NamespaceFormats = typeof namespaceFormats;

export type Namespace = keyof NamespaceFormats;

registerTableCellFormats(namespaceFormats.tableCell);

export default namespaceFormats;
